// Auto-mapping: from a pasted example (a few records, or just field names) to
// a generated Mapping spec. Everything here is a pure function of the text;
// the generated rules use only what the three engines already agree on
// (path / cast / timestamp / labels / outputs), so the preview is exact.
import { parseTimestamp, resolvePath, type JsonValue, type MappingRule, type MappingSpec } from './mappingEngine';
import { parseCsvSample } from './mappingPreview';
import { flattenPaths } from './mappingSpecEdit';
import { isPromSampleLine, parsePrometheusText, parseTextStream, type PromSample } from './sampleText';
import { parseSimpleYaml } from './simpleYaml';
import { xmlToRecord } from './xmlRecord';

export type AutoFormat = 'json' | 'csv' | 'tsv' | 'xml' | 'yaml' | 'stdin' | 'stdout' | 'stderr' | 'prometheus' | 'html';
export type AutoType = 'webhook' | 'file' | 'agent' | 'scraper';

export const AUTO_FORMATS: { id: AutoFormat; label: string }[] = [
  { id: 'json', label: 'JSON' },
  { id: 'csv', label: 'CSV' },
  { id: 'tsv', label: 'TSV' },
  { id: 'xml', label: 'XML' },
  { id: 'yaml', label: 'YAML' },
  { id: 'stdin', label: 'STDIN' },
  { id: 'stdout', label: 'STDOUT' },
  { id: 'stderr', label: 'STDERR' },
  { id: 'prometheus', label: 'Prometheus metrics' },
  { id: 'html', label: 'HTML table' },
];

export const AUTO_TYPES: { id: AutoType; label: string; hint: string }[] = [
  { id: 'webhook', label: 'Webhook', hint: 'Your system POSTs the data to a Zmist URL.' },
  { id: 'file', label: 'File', hint: 'The cue-cli agent reads a local file on your machine.' },
  { id: 'agent', label: 'Agent', hint: 'The cue-cli agent polls an HTTP endpoint you can reach.' },
  { id: 'scraper', label: 'Scraper', hint: 'Zmist fetches the URL on a schedule - no agent needed.' },
];

// What the platform can actually RUN today, format x type -> Mapping sourceType.
// A missing entry means "mapping can be generated and previewed, but nothing
// would ingest it yet" - the screen says so instead of creating a dead
// integration. The `scraper` column (internal/scrapequeue) is server-side
// polling - Zmist itself fetches spec.target on a schedule, distinct
// sourceTypes (scrape_*) from the agent_pull ones so the backend can tell
// the two apart (see internal/integration.Resolve's comment).
const RUNNABLE: Partial<Record<AutoFormat, Partial<Record<AutoType, string>>>> = {
  json: { webhook: 'webhook', file: 'json', agent: 'json', scraper: 'scrape_json' },
  xml: { webhook: 'webhook', file: 'xml', agent: 'xml', scraper: 'scrape_xml' },
  csv: { webhook: 'webhook', file: 'csv', agent: 'csv', scraper: 'scrape_csv' },
  tsv: { webhook: 'webhook', file: 'tsv', agent: 'tsv', scraper: 'scrape_tsv' },
  yaml: { webhook: 'webhook', file: 'yaml', agent: 'yaml', scraper: 'scrape_yaml' },
  // Text streams (STDIN/STDOUT/STDERR captures, log files): one Record per line.
  stdin: { webhook: 'webhook', file: 'lines', agent: 'lines' },
  stdout: { webhook: 'webhook', file: 'lines', agent: 'lines' },
  stderr: { webhook: 'webhook', file: 'lines', agent: 'lines' },
  prometheus: { agent: 'prometheus', scraper: 'scrape_prometheus' },
  // HTML table scraping only makes sense server-side (the backend fetches
  // and parses the page) - no webhook/file/agent equivalent.
  html: { scraper: 'scrape_html' },
};

export const runnableSourceType = (f: AutoFormat, t: AutoType): string | undefined => RUNNABLE[f]?.[t];

// The Record format a webhook should read its body as when the sender's
// Content-Type doesn't say (stored as spec.format; the receiver, the agent and
// the browser preview all honour it). undefined = JSON, the receiver's default.
export function webhookBodyFormat(f: AutoFormat): string | undefined {
  if (f === 'stdin' || f === 'stdout' || f === 'stderr') return 'lines';
  return f === 'json' || f === 'prometheus' ? undefined : f;
}

export function unsupportedReason(f: AutoFormat, t: AutoType): string {
  const label = AUTO_FORMATS.find((x) => x.id === f)?.label ?? f;
  if (f === 'prometheus' && t === 'webhook') return 'Prometheus metrics arrive via an agent scrape, a Scraper poll, or Remote Write, not a generic webhook.';
  if (f === 'prometheus') return 'Prometheus metrics are polled, not pushed - choose Agent or Scraper.';
  if (f === 'html') return 'HTML tables are fetched by Zmist itself - choose Scraper.';
  return `${label} via ${t} isn't supported.`;
}

// --- format detection + parsing -------------------------------------------

const IDENT_LIST = /^[A-Za-z_@#][\w.@#-]*([\s,;\t]+[A-Za-z_@#][\w.@#-]*)*$/;

export function detectAutoFormat(text: string): AutoFormat {
  const t = text.trim();
  if (t.startsWith('<')) return /^<!doctype html|^<html[\s>]|<table[\s>]/i.test(t) ? 'html' : 'xml';
  if (t.startsWith('{') || t.startsWith('[')) return 'json';
  const lines = t.split(/\r?\n/).filter((l) => l.trim() && !l.trim().startsWith('#'));
  const raw = t.split(/\r?\n/);
  if (raw.some((l) => /^#\s*(TYPE|HELP)\s/.test(l)) || (lines.length > 0 && lines.every(isPromSampleLine) && /[{]|_/.test(lines[0]))) return 'prometheus';
  if (/^(-\s|-$|[\w"'.-]+:(\s|$))/.test(lines[0] ?? '') && !/=/.test(lines[0] ?? '')) return 'yaml';
  if (lines.some((l) => l.includes('\t'))) return 'tsv';
  if (lines.some((l) => l.includes(','))) return 'csv';
  if (lines.length && lines.every((l) => l.trim().startsWith('{'))) return 'json';
  if (IDENT_LIST.test(t)) return 'csv';
  return 'stdout';
}

export interface ParsedSample {
  format: AutoFormat;
  records: JsonValue[];
  recordPath?: string;
  fieldsOnly: boolean;
  prom?: PromSample[];
}

const isObj = (v: unknown): v is Record<string, JsonValue> => v !== null && typeof v === 'object' && !Array.isArray(v);

// Where the repeating records live inside a parsed document: the root array,
// or the first array of objects at depth <= 3 (recordPath, dot-notation).
function locateRecords(root: JsonValue): { records: JsonValue[]; recordPath?: string } {
  if (Array.isArray(root)) return { records: root.filter(isObj) };
  if (!isObj(root)) throw new Error('The sample has to contain objects (records), not just a bare value.');
  const queue: [string, JsonValue, number][] = [['', root, 0]];
  while (queue.length) {
    const [path, node, depth] = queue.shift()!;
    if (Array.isArray(node) && node.length > 0 && node.every(isObj) && path) return { records: node as JsonValue[], recordPath: path };
    if (isObj(node) && depth < 3) for (const [k, v] of Object.entries(node)) queue.push([path ? `${path}.${k}` : k, v, depth + 1]);
  }
  return { records: [root] };
}

// HTML table extraction for the preview - a dependency-free regex scan (no
// DOMParser, same "runs in tests and a worker alike" reasoning as
// xmlRecord.ts), scoped to the first <table> in the document. Must agree
// with the backend's mappingengine.HTMLTableToRecords
// (internal/mappingengine/html.go) on the record shape: the first row's
// cell text names the fields, one record per following row. Nested tables
// aren't handled, same documented limitation as the Go version.
const HTML_ROW = /<tr\b[^>]*>([\s\S]*?)<\/tr\s*>/gi;
const HTML_CELL = /<(td|th)\b[^>]*>([\s\S]*?)<\/(?:td|th)\s*>/gi;
const HTML_TAG = /<[^>]*>/g;
const HTML_ENTITIES: Record<string, string> = { nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", apos: "'" };

function htmlCellText(cellHTML: string): string {
  const stripped = cellHTML.replace(HTML_TAG, ' ').replace(/&(nbsp|amp|lt|gt|quot|#39|apos);/g, (_, e: string) => HTML_ENTITIES[e]);
  return stripped.trim().replace(/\s+/g, ' ');
}

function htmlFirstTableRecords(text: string): JsonValue[] {
  const withoutComments = text.replace(/<!--[\s\S]*?-->/g, '');
  const tableMatch = /<table\b[^>]*>([\s\S]*?)<\/table\s*>/i.exec(withoutComments);
  if (!tableMatch) throw new Error('No <table> found in the pasted HTML.');

  const rows: string[][] = [];
  for (const rowMatch of tableMatch[1].matchAll(HTML_ROW)) {
    const cells: string[] = [];
    for (const cellMatch of rowMatch[1].matchAll(HTML_CELL)) cells.push(htmlCellText(cellMatch[2]));
    if (cells.length > 0) rows.push(cells);
  }
  if (rows.length === 0) throw new Error('The table has no rows.');

  const header = rows[0];
  const dataRows = rows.length > 1 ? rows.slice(1) : rows;
  return dataRows.map((row) => {
    const rec: Record<string, JsonValue> = {};
    header.forEach((key, i) => {
      rec[key || `col${i + 1}`] = row[i] ?? '';
    });
    return rec;
  });
}

// recordPath (json only): the list the user chose inside a bigger document.
export function parseAutoSample(format: AutoFormat, text: string, recordPath?: string): ParsedSample {
  const t = text.trim();
  if (!t) return { format, records: [], fieldsOnly: false };
  switch (format) {
    case 'json': {
      try {
        const doc = JSON.parse(t) as JsonValue;
        const chosen = recordPath ? resolvePath(doc, recordPath) : undefined;
        if (Array.isArray(chosen) && chosen.some(isObj)) return { format, fieldsOnly: false, records: chosen.filter(isObj), recordPath };
        return { format, fieldsOnly: false, ...locateRecords(doc) };
      } catch (e) {
        // newline-delimited JSON
        const lines = t.split(/\r?\n/).filter((l) => l.trim());
        if (lines.length > 1) {
          try {
            return { format, fieldsOnly: false, records: lines.map((l) => JSON.parse(l) as JsonValue).filter(isObj) };
          } catch {
            /* report the original error */
          }
        }
        throw e instanceof Error ? new Error(`Not valid JSON: ${e.message}`) : e;
      }
    }
    case 'yaml':
      return { format, fieldsOnly: false, ...locateRecords(parseSimpleYaml(t)) };
    case 'xml':
      return { format, fieldsOnly: false, ...locateRecords(xmlToRecord(t)) };
    case 'csv':
    case 'tsv': {
      const delim = format === 'tsv' ? '\t' : ',';
      const nonEmpty = t.split(/\r?\n/).filter((l) => l.trim());
      // A single line, or a plain list of names, is "just the fields": no data yet.
      const asNames = nonEmpty.length === 1 || (nonEmpty.length > 1 && IDENT_LIST.test(t));
      if (asNames) {
        const names = t.split(/[\s,;\t]+/).filter(Boolean);
        return { format, fieldsOnly: true, records: [Object.fromEntries(names.map((n) => [n, '']))] };
      }
      return { format, fieldsOnly: false, records: parseCsvSample(t, delim) };
    }
    case 'stdin':
    case 'stdout':
    case 'stderr':
      return { format, fieldsOnly: false, records: parseTextStream(t) };
    case 'prometheus': {
      const prom = parsePrometheusText(t);
      // Each sample as a record, so the same field-mapping UI/engine as JSON/CSV applies:
      // {name, value, labels:{k:v}} - the agent builds the identical shape before mapping.
      const records: JsonValue[] = prom.map((p) => ({ name: p.name, value: p.value, labels: { ...p.labels } }));
      return { format, fieldsOnly: false, records, prom };
    }
    case 'html':
      return { format, fieldsOnly: false, records: htmlFirstTableRecords(t) };
  }
}

// --- mapping generation ---------------------------------------------------

export type FieldRole = 'time' | 'name' | 'value' | 'label' | 'skipped';

// The doc's three-band confidence model (AUTOMATIC_SEMANTIC_SIGNAL_MAPPING.md
// "Confidence Model"): >= AUTO_THRESHOLD auto-applies, >= SUGGEST_THRESHOLD
// is shown but not silently trusted, below that is a guess made only because
// no example data was available (fieldsOnly) or the signal was genuinely
// ambiguous (e.g. DD/MM vs MM/DD). Exported so callers/tests can reason about
// bands without hard-coding the cutoffs a second time.
export const AUTO_THRESHOLD = 0.9;
export const SUGGEST_THRESHOLD = 0.6;

export type ConfidenceLevel = 'High' | 'Medium' | 'Low';
export function confidenceLevel(score: number): ConfidenceLevel {
  if (score >= AUTO_THRESHOLD) return 'High';
  if (score >= SUGGEST_THRESHOLD) return 'Medium';
  return 'Low';
}

export interface FieldAlternative {
  role: FieldRole;
  confidence: number;
  detail: string;
}

export interface FieldReport {
  path: string;
  role: FieldRole;
  detail: string;
  // 0..1. A guess made from the field's name alone (no example value to
  // confirm it, e.g. `fieldsOnly` mode) is capped below SUGGEST_THRESHOLD -
  // the doc's point that mapping is never simply correct/incorrect.
  confidence: number;
  // Other roles this field could plausibly have played, most confident
  // first - only populated when genuinely ambiguous (e.g. DD/MM vs MM/DD).
  alternatives?: FieldAlternative[];
}
export interface AutoMapping {
  spec: MappingSpec; // rules/outputs (+ recordPath) only - the screen adds channel/target/etc.
  fields: FieldReport[];
  notes: string[];
}

const TIME_NAME = /^(ts|time|timestamp|time_?stamp|at|datetime|date_?time|date|created_?at|updated_?at|occurred_?at|event_?time|logged_?at|when)$|(_at|_time|_ts|_date)$/i;
const NAME_FIELD = /^(name|metric|metric_?name|event|event_?name|type|kind|check|indicator|series)$/i;
const VALUE_FIELD = /^(value|val|v|amount|count|total|reading|metric_?value|measurement)$/i;
const NUMERIC_HINT = /(count|total|amount|value|pct|percent|bytes|ms|seconds|latency|duration|size|rate|temp|cpu|mem|load|price|qty|quantity|score|level|usage)/i;
const SENSITIVE = /(^|_)(password|passwd|secret|token|apikey|api_key|authorization|auth|cookie)($|_)/i;
const IDLIKE = /(^|_)(id|uuid|guid|hash|sha|trace|span|request_?id)$/i;

const TIME_PATTERNS = [
  'rfc3339',
  'YYYY-MM-DD HH:mm:ss.SSS',
  'YYYY-MM-DD HH:mm:ssZ',
  'YYYY-MM-DD HH:mm:ss',
  'YYYY/MM/DD HH:mm:ss',
  'DD.MM.YYYY HH:II:ss',
  'DD.MM.YY HH:II:ss',
  'DD/MM/YYYY HH:mm:ss',
  'MM/DD/YYYY HH:mm:ss',
  'DD-MM-YYYY HH:mm:ss',
  'YYYY-MM-DD',
  'DD.MM.YYYY',
];

const NUM = /^[-+]?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?$/;
const asText = (v: string) => v.trim();

// Transformation inference (doc's "Transformation Inference": price_cents ->
// value/100 + unit, ms -> s + unit): a numeric field named like a
// sub-unit money or duration count gets the matching `unitScale`, plus a
// `labels.unit` rule so the scaled value still carries what it's *in* -
// Zmist's Signal record has no top-level unit/type field (only
// name/value/at/labels), so unit rides along as a label instead of a new
// Signal field.
interface ValueTransform {
  unitScale: number;
  unit: string;
}
function inferValueTransform(leaf: string): ValueTransform | undefined {
  if (/(^|_)cents$/i.test(leaf)) return { unitScale: 0.01, unit: 'USD' };
  if (/(^|_)ms$/i.test(leaf)) return { unitScale: 0.001, unit: 's' };
  return undefined;
}

// The `timestamp` format that parses EVERY non-empty sample, or undefined.
export function detectTimestampFormat(samples: string[]): { format: string; ambiguous?: boolean } | undefined {
  const vals = samples.map(asText).filter(Boolean);
  if (!vals.length) return undefined;
  if (vals.every((v) => NUM.test(v))) {
    if (vals.every((v) => /^\d{13}(\.\d+)?$/.test(v) && Number(v) > 9.4e11 && Number(v) < 4.1e12)) return { format: 'unix_ms' };
    if (vals.every((v) => /^\d{10}(\.\d+)?$/.test(v) && Number(v) > 9.4e8 && Number(v) < 4.1e9)) return { format: 'unix_s' };
    return undefined;
  }
  const ok = TIME_PATTERNS.filter((f) => vals.every((v) => parseTimestamp(v, f) !== null));
  if (!ok.length) return undefined;
  const format = ok[0];
  const ambiguous = format === 'DD/MM/YYYY HH:mm:ss' && ok.includes('MM/DD/YYYY HH:mm:ss');
  return { format, ambiguous };
}

const safeKey = (s: string) => s.replace(/[^A-Za-z0-9_]/g, '_').replace(/^_+|_+$/g, '') || 'field';

interface Col {
  path: string;
  samples: string[];
  jsonNumbers: boolean;
  typedAllNumbers: boolean;
}

// Prometheus samples are already structured, so no guessing: the metric name
// names the Signal, the sample value is its value, every label is kept as a
// label. The user re-points any of it in the same editor as other formats.
// Splits a field/const name into lowercase words (snake_case, camelCase, and
// plain separators all count), so "cpu_pct" and "CPU %" compare as {cpu, pct}
// vs {cpu} rather than failing a literal string match.
const WORD_RE = /[A-Z]+(?=[A-Z][a-z])|[A-Z]?[a-z]+|[A-Z]+|\d+/g;
function nameWords(s: string): Set<string> {
  return new Set((s.match(WORD_RE) ?? []).map((w) => w.toLowerCase()));
}

// The best existing Signal name a generated name (a field's own key, e.g.
// from `generateMapping`'s per-field consts) could stand for - used to
// pre-point a new mapping's outputs at an existing template's Signals
// instead of minting new, near-duplicate names. Exact (case-insensitive)
// match wins outright; otherwise the existing name with the most overlapping
// words, as long as it shares at least a third of its words with the
// candidate. Returns undefined rather than guess when nothing is close.
export function matchExistingSignalName(candidate: string, existing: string[]): string | undefined {
  if (!candidate || existing.length === 0) return undefined;
  const lower = candidate.toLowerCase();
  const exact = existing.find((n) => n.toLowerCase() === lower);
  if (exact) return exact;
  const cWords = nameWords(candidate);
  if (cWords.size === 0) return undefined;
  let best: { name: string; score: number } | undefined;
  for (const name of existing) {
    const nWords = nameWords(name);
    if (nWords.size === 0) continue;
    const overlap = [...cWords].filter((w) => nWords.has(w)).length;
    if (overlap === 0) continue;
    const score = overlap / new Set([...cWords, ...nWords]).size;
    if (!best || score > best.score) best = { name, score };
  }
  return best && best.score >= 1 / 3 ? best.name : undefined;
}

export function generatePromMapping(records: JsonValue[]): AutoMapping {
  const keys: string[] = [];
  for (const r of records) {
    const labels = isObj(r) && isObj(r.labels) ? r.labels : {};
    for (const k of Object.keys(labels)) if (!keys.includes(k)) keys.push(k);
  }
  const rules: MappingRule[] = [
    { to: 'name', from: 'name' },
    { to: 'value', from: 'value', cast: 'number' },
    ...keys.map((k): MappingRule => ({ to: `labels.${k}`, from: `labels.${k}` })),
  ];
  const fields: FieldReport[] = [
    { path: 'name', role: 'name', detail: 'metric name - names the Signal', confidence: 0.99 },
    { path: 'value', role: 'value', detail: 'sample value', confidence: 0.99 },
    ...keys.map((k): FieldReport => ({ path: `labels.${k}`, role: 'label', detail: `label ${k}`, confidence: 0.97 })),
  ];
  return { spec: { rules }, fields, notes: [] };
}

export function generateMapping(records: JsonValue[], fieldsOnly: boolean): AutoMapping {
  const notes: string[] = [];
  const cols = new Map<string, Col>();
  const sample = records.slice(0, 50);
  for (const rec of sample) {
    for (const { path, sample: s } of flattenPaths(rec)) {
      const c = cols.get(path) ?? { path, samples: [], jsonNumbers: true, typedAllNumbers: true };
      c.samples.push(s);
      cols.set(path, c);
    }
  }
  // Whether the raw JSON values were numbers or numeric strings decides `cast`.
  for (const rec of sample) {
    const walk = (v: JsonValue, prefix: string) => {
      if (Array.isArray(v)) return;
      if (isObj(v)) return Object.entries(v).forEach(([k, c]) => walk(c, prefix ? `${prefix}.${k}` : k));
      const c = cols.get(prefix);
      if (c && typeof v !== 'number') c.jsonNumbers = false;
    };
    walk(rec, '');
  }

  const fields: FieldReport[] = [];
  const rules: { time?: MappingRule; name?: { path: string }; label: MappingRule[]; numeric: Col[] } = { label: [], numeric: [] };
  let timeCol: Col | undefined;
  let timeFmt: string | undefined;
  if (fieldsOnly) notes.push('Only field names were given, so types were guessed from the names - paste a row or two of real data for an exact mapping.');
  const skip = (c: Col, detail: string, confidence = 0.9) => fields.push({ path: c.path, role: 'skipped', detail, confidence });
  // Whether a column has an actual example value to confirm the decision, or
  // the decision was made from the field's name/shape alone (fieldsOnly, or a
  // field with no non-empty samples in the pasted rows) - the doc's line
  // between a confirmed mapping and a guess that still needs a human look.
  const hasRealData = (c: Col) => c.samples.some((s) => s.trim() !== '');
  const numericConfidence = (c: Col) => {
    const base = hasRealData(c) ? 0.95 : 0.55;
    // A unit/scale is guessed from the field's name alone (no way to confirm
    // "cents" really means USD cents from the value shape) - always a
    // SUGGEST-band guess even when the number itself is data-confirmed.
    return inferValueTransform(c.path.split('.').pop() ?? c.path) ? Math.min(base, 0.82) : base;
  };
  const labelConfidence = (c: Col) => (hasRealData(c) ? 0.8 : 0.5);
  const nameConfidence = (c: Col) => (hasRealData(c) ? 0.9 : 0.6);

  const list = [...cols.values()].filter((c) => {
    if (c.path.split('.').some((seg) => /^\d+$/.test(seg))) {
      skip(c, 'inside a list - map it by hand in the editor', 0.95);
      return false;
    }
    if (SENSITIVE.test(c.path.split('.').pop() ?? '')) {
      skip(c, 'looks like a secret - never mapped', 0.85);
      return false;
    }
    return true;
  });

  // 1. time: a field whose every example parses as a timestamp (a bare
  // 10/13-digit number only counts when it is also NAMED like a time), then,
  // with no example data at all, a field named like one.
  const candidates: { c: Col; format: string; ambiguous?: boolean; named: boolean }[] = [];
  for (const c of list) {
    const leaf = c.path.split('.').pop() ?? c.path;
    const named = TIME_NAME.test(leaf);
    const found = c.samples.some((s) => s.trim() !== '') ? detectTimestampFormat(c.samples) : undefined;
    if (found && (named || !found.format.startsWith('unix'))) candidates.push({ c, ...found, named });
  }
  candidates.sort((x, y) => Number(y.named) - Number(x.named));
  if (candidates.length) {
    const best = candidates[0];
    timeCol = best.c;
    timeFmt = best.format;
    if (best.ambiguous) notes.push(`"${best.c.path}" reads as DD/MM/YYYY; if your dates are MM/DD/YYYY, change the format in the editor.`);
  } else {
    const byName = list.find((c) => c.samples.every((s) => s.trim() === '') && TIME_NAME.test(c.path.split('.').pop() ?? ''));
    if (byName) {
      timeCol = byName;
      timeFmt = 'rfc3339';
      notes.push(`No example value for "${byName.path}", so RFC 3339 was assumed - change the format in the editor if yours differs.`);
    }
  }
  if (timeCol) {
    rules.time = { to: 'at', from: timeCol.path, timestamp: timeFmt };
    const best = candidates.find((x) => x.c === timeCol);
    let confidence = best ? (best.named ? 0.97 : 0.82) : 0.5; // no `best` = the no-data byName fallback, a pure guess
    let alternatives: FieldAlternative[] | undefined;
    if (best?.ambiguous) {
      confidence = Math.min(confidence, 0.75); // DD/MM vs MM/DD - both parse, only one is right
      alternatives = [{ role: 'time', confidence: confidence - 0.05, detail: `could also be MM/DD/YYYY, not ${timeFmt}` }];
    }
    fields.push({ path: timeCol.path, role: 'time', detail: `timestamp (${timeFmt})`, confidence, ...(alternatives ? { alternatives } : {}) });
  } else {
    notes.push('No time field found - Signals will be stamped with the moment they arrive.');
  }

  // 2. classify the rest.
  const rest = list.filter((c) => c !== timeCol);
  let nameCol: Col | undefined;
  let valueCol: Col | undefined;
  for (const c of rest) {
    const leaf = c.path.split('.').pop() ?? c.path;
    const hasData = c.samples.some((s) => s.trim() !== '');
    const vals = c.samples.map(asText).filter(Boolean);
    const numeric = hasData ? vals.length > 0 && vals.every((v) => NUM.test(v)) : NUMERIC_HINT.test(leaf) || VALUE_FIELD.test(leaf);
    const idlike = IDLIKE.test(leaf);
    const distinct = new Set(vals).size;
    if (numeric && !idlike) {
      rules.numeric.push({ ...c, typedAllNumbers: c.jsonNumbers });
    } else if (idlike || vals.some((v) => v.length > 64) || (sample.length >= 5 && distinct === vals.length && !NAME_FIELD.test(leaf))) {
      skip(c, idlike ? 'identifier - too many distinct values to be a label' : 'unique or long values - not useful as a label', idlike ? 0.85 : 0.7);
    } else if (!nameCol && NAME_FIELD.test(leaf)) {
      nameCol = c;
    } else if (rules.label.length < 8) {
      const key = safeKey(leaf);
      const taken = rules.label.some((r) => r.to === `labels.${key}`);
      rules.label.push({ to: `labels.${taken ? safeKey(c.path) : key}`, from: c.path });
      // Enum translation (doc's "Transformation Inference"): a low-cardinality
      // text field looks like a fixed set of outcomes/statuses. Never guessed
      // silently (the doc's own example: "failed" could mean many things) -
      // just pointed out, so the user can add a `map` rule in the editor.
      const distinctVals = [...new Set(vals)];
      const looksEnum = hasData && !c.jsonNumbers && distinctVals.length >= 2 && distinctVals.length <= 6 && vals.length >= 3;
      if (looksEnum) notes.push(`"${c.path}" looks like a fixed set of values (${distinctVals.slice(0, 6).join(', ')}) - add a \`map\` rule in the editor to translate them.`);
      fields.push({
        path: c.path,
        role: 'label',
        detail: looksEnum ? `label ${taken ? safeKey(c.path) : key} - looks like an enum (${distinctVals.join('/')})` : `label ${taken ? safeKey(c.path) : key}`,
        confidence: labelConfidence(c),
      });
    } else {
      skip(c, 'more than 8 labels - the rest are left out', 0.6);
    }
  }

  const valueRule = (c: Col): MappingRule => {
    const leaf = c.path.split('.').pop() ?? c.path;
    const t = inferValueTransform(leaf);
    return { to: 'value', from: c.path, ...(c.typedAllNumbers && c.samples.some((s) => s.trim() !== '') ? {} : { cast: 'number' }), ...(t ? { unitScale: t.unitScale } : {}) };
  };
  // A money/duration field's own output also carries where its scaled value
  // now lives, unit-wise - the doc's `signal.unit = "USD"` example, expressed
  // as a label since Zmist Signals have no top-level unit field. Prefers a
  // sibling `currency` field's own value over a hard-coded "USD" guess.
  const currencyCol = list.find((c) => /(^|\.)currency$/i.test(c.path));
  const extraRulesFor = (c: Col): MappingRule[] => {
    const t = inferValueTransform(c.path.split('.').pop() ?? c.path);
    if (!t) return [];
    if (t.unit === 'USD' && currencyCol) return [{ to: 'labels.unit', from: currencyCol.path }];
    return [{ to: 'labels.unit', const: t.unit }];
  };
  const shared = (): MappingRule[] => [...(rules.time ? [{ ...rules.time }] : []), ...rules.label.map((r) => ({ ...r }))];

  // "name"+"value" style records -> one output whose Signal name comes from data.
  if (nameCol && rules.numeric.length > 0) {
    valueCol = rules.numeric.find((c) => VALUE_FIELD.test(c.path.split('.').pop() ?? '')) ?? rules.numeric[0];
    fields.push({ path: nameCol.path, role: 'name', detail: 'Signal name comes from this field', confidence: nameConfidence(nameCol) });
    fields.push({ path: valueCol.path, role: 'value', detail: 'Signal value', confidence: numericConfidence(valueCol) });
    for (const c of rules.numeric.filter((x) => x !== valueCol)) {
      const leaf = c.path.split('.').pop() ?? c.path;
      rules.label.push({ to: `labels.${safeKey(leaf)}`, from: c.path });
      fields.push({ path: c.path, role: 'label', detail: 'extra number kept as a label', confidence: labelConfidence(c) });
    }
    return { spec: { rules: [{ to: 'name', from: nameCol.path }, valueRule(valueCol), ...extraRulesFor(valueCol), ...shared()] }, fields, notes };
  }

  // A name-like text field but no number: it names the event.
  if (nameCol) {
    fields.push({ path: nameCol.path, role: 'name', detail: 'Signal name comes from this field', confidence: nameConfidence(nameCol) });
    notes.push('No numeric field found - Signals will carry labels only, no value.');
    return { spec: { rules: [{ to: 'name', from: nameCol.path }, ...shared()] }, fields, notes };
  }

  if (rules.numeric.length === 0) {
    notes.push('No numeric field found - Signals will carry labels only, no value.');
    return { spec: { rules: [{ to: 'name', const: 'event' }, ...shared()] }, fields, notes };
  }

  const nums = rules.numeric.slice(0, 20);
  if (rules.numeric.length > 20) notes.push(`${rules.numeric.length} numeric fields found - the first 20 became Signals.`);
  for (const c of nums) fields.push({ path: c.path, role: 'value', detail: `Signal "${safeKey(c.path.split('.').pop() ?? c.path)}"`, confidence: numericConfidence(c) });
  if (nums.length === 1) {
    return { spec: { rules: [{ to: 'name', const: safeKey(nums[0].path.split('.').pop() ?? nums[0].path) }, valueRule(nums[0]), ...extraRulesFor(nums[0]), ...shared()] }, fields, notes };
  }
  notes.push('Several numeric fields: one Signal is produced per field, sharing the time and labels.');
  return {
    spec: { outputs: nums.map((c) => ({ rules: [{ to: 'name', const: safeKey(c.path.split('.').pop() ?? c.path) } as MappingRule, valueRule(c), ...extraRulesFor(c), ...shared()] })) },
    fields,
    notes,
  };
}
